import { useEffect, useState } from "react";
import sellerApi from "@/lib/sellerApi";
import { useSellerAuth } from "@/context/SellerAuthContext";
import { fileUrl } from "@/lib/api";
import { rupee } from "@/lib/format";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Package, CheckCircle2, AlertTriangle, ShoppingCart, Clock3, IndianRupee, XCircle, Store, MapPin } from "lucide-react";

export default function SellerDashboard() {
  usePageMeta("Seller Dashboard", "Manage your NICEE shop.");
  const { seller } = useSellerAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    sellerApi.get("/seller/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!seller) return null;
  const o = stats?.orders || {};

  const cards = [
    { label: "Total Products", value: stats?.total_products ?? 0, icon: Package, testid: "stat-total-products" },
    { label: "Active Products", value: stats?.active_products ?? 0, icon: CheckCircle2, testid: "stat-active-products" },
    { label: "Out of Stock", value: stats?.out_of_stock ?? 0, icon: AlertTriangle, testid: "stat-out-of-stock" },
    { label: "New Orders", value: o.NEW ?? 0, icon: ShoppingCart, testid: "stat-new-orders" },
    { label: "Pending Orders", value: stats?.pending_orders ?? 0, icon: Clock3, testid: "stat-pending-orders" },
    { label: "Completed", value: o.COMPLETED ?? 0, icon: CheckCircle2, testid: "stat-completed" },
    { label: "Cancelled", value: o.CANCELLED ?? 0, icon: XCircle, testid: "stat-cancelled" },
    { label: "Total Sales", value: rupee(stats?.total_sales ?? 0), icon: IndianRupee, testid: "stat-total-sales" },
  ];

  return (
    <div className="space-y-8" data-testid="seller-dashboard">
      <div className="flex items-center gap-4">
        {seller.logo_id ? (
          <img src={fileUrl(seller.logo_id)} alt={seller.shop_name} className="h-14 w-14 rounded-2xl object-cover" />
        ) : (
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/15 text-brand"><Store className="h-7 w-7" /></span>
        )}
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink">{seller.shop_name}</h1>
          <p className="text-sm text-ink-soft">Welcome back, {seller.owner_name}</p>
        </div>
        <span className="ml-auto rounded-full bg-sage/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-sage">Approved</span>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border/60 bg-white p-5 shadow-ambient" data-testid={c.testid}>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand"><c.icon className="h-5 w-5" /></span>
            <p className="mt-3 font-heading text-2xl font-bold text-ink">{c.value}</p>
            <p className="text-sm text-ink-muted">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
        <h2 className="font-heading text-lg font-bold text-ink">Order pipeline</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {["NEW", "ACCEPTED", "PREPARING", "READY", "COMPLETED"].map((s) => (
            <div key={s} className="rounded-xl bg-cream-alt/50 p-3 text-center">
              <p className="font-heading text-xl font-bold text-ink">{o[s] ?? 0}</p>
              <p className="text-xs text-ink-muted">{s}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
        <h2 className="font-heading text-lg font-bold text-ink">Shop</h2>
        <p className="mt-2 flex items-start gap-2 text-sm text-ink-soft"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" /> {[seller.address, seller.area, seller.city, seller.state, seller.pincode].filter(Boolean).join(", ")}</p>
        <p className="mt-1 text-sm text-ink-soft">WhatsApp for orders: {seller.whatsapp_number}</p>
      </div>
    </div>
  );
}
